import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoIcon from "@mui/icons-material/Info";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import JsonDataModal from "../components/admin/JsonDataModal";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { SUBSCRIPTION_PLANS_VENDORS } from "../constants/admin";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const DISPLAY_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function SquarespaceProcessOrder() {
  const api = useApi();
  const [orderJson, setOrderJson] = React.useState("");
  const [processing, setProcessing] = React.useState(false);
  const [validating, setValidating] = React.useState(false);
  const [validation, setValidation] = React.useState({
    errors: [],
    warnings: [],
    status: null, // 'failed' | 'validated' | null
  });
  const [isValidJson, setIsValidJson] = React.useState(true);
  const [steps, setSteps] = React.useState([]);
  const [messages, setMessages] = React.useState([]);
  const [validationJsonModalOpen, setValidationJsonModalOpen] = React.useState(false);
  const [copiedUrl, setCopiedUrl] = React.useState(false);
  const [copiedToken, setCopiedToken] = React.useState(false);
  const [orgModalOpen, setOrgModalOpen] = React.useState(false);
  const [orgList, setOrgList] = React.useState([]);
  const [orgLoading, setOrgLoading] = React.useState(false);
  const [orgError, setOrgError] = React.useState(null);
  const [orgSearch, setOrgSearch] = React.useState("");
  const [selectedOrgId, setSelectedOrgId] = React.useState("");

  const [planModalOpen, setPlanModalOpen] = React.useState(false);
  const [planList, setPlanList] = React.useState([]);
  const [planLoading, setPlanLoading] = React.useState(false);
  const [planError, setPlanError] = React.useState(null);
  const [selectedPlanId, setSelectedPlanId] = React.useState("");
  const [planVendorFilter, setPlanVendorFilter] = React.useState("");
  const [planSearch, setPlanSearch] = React.useState("");

  const processOrderEndpoint = `${DISPLAY_BASE}/admin/squarespace/orders/process`;
  const orderToken = import.meta.env.VITE_ORDER_TOKEN || "";
  const processOrderUrlWithToken = orderToken
    ? `${processOrderEndpoint}?token=${encodeURIComponent(orderToken)}`
    : processOrderEndpoint;

  const handleCopyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(processOrderUrlWithToken);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(orderToken);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const addStep = (stepName, status = "pending") => {
    setSteps((prev) => [...prev, { name: stepName, status, timestamp: new Date() }]);
  };

  const updateStep = (stepName, status) => {
    setSteps((prev) =>
      prev.map((step) => (step.name === stepName ? { ...step, status, timestamp: new Date() } : step))
    );
  };

  const addMessage = (message, type = "info", options = {}) => {
    setMessages((prev) => [...prev, { message, type, timestamp: new Date(), ...options }]);
  };

  const clearStepsAndMessages = () => {
    setSteps([]);
    setMessages([]);
  };

  // Validate JSON format in real-time
  const validateJsonFormat = (jsonString) => {
    if (!jsonString.trim()) {
      setIsValidJson(true); // Allow empty for initial state
      return true;
    }
    try {
      JSON.parse(jsonString);
      setIsValidJson(true);
      return true;
    } catch {
      setIsValidJson(false);
      return false;
    }
  };

  const handleJsonChange = (e) => {
    const newValue = e.target.value;
    setOrderJson(newValue);
    validateJsonFormat(newValue);
    // Clear validation state when user edits JSON
    setValidation({
      errors: [],
      warnings: [],
      status: null,
    });
  };

  const getDefaultTemplate = () => ({
    email: "",
    firstName: "John",
    lastName: "Doe",
    country: "US",
    ipAddress: "192.168.1.1",
    title: "My New Website",
    organization_id: "00000000-0000-0000-0000-000000000000",
    subscription_plan_id: "00000000-0000-0000-0000-000000000000",
  });

  const mergeIntoPayload = (updates) => {
    let payload;
    if (!orderJson.trim()) {
      payload = { ...getDefaultTemplate(), ...updates };
    } else {
      try {
        payload = { ...JSON.parse(orderJson), ...updates };
      } catch {
        payload = { ...getDefaultTemplate(), ...updates };
      }
    }
    setOrderJson(JSON.stringify(payload, null, 2));
    validateJsonFormat(JSON.stringify(payload, null, 2));
    setValidation({ errors: [], warnings: [], status: null });
  };

  const fetchOrganizations = React.useCallback(async () => {
    setOrgLoading(true);
    setOrgError(null);
    setSelectedOrgId("");
    try {
      const params = new URLSearchParams({ limit: "100", offset: "0" });
      if (orgSearch.trim()) params.append("search", orgSearch.trim());
      const data = await api.get(`/admin/organizations?${params}`);
      setOrgList(data.data || []);
    } catch (err) {
      setOrgError(err.message || "Failed to fetch organizations");
    } finally {
      setOrgLoading(false);
    }
  }, [api, orgSearch]);

  const fetchSubscriptionPlans = React.useCallback(async () => {
    setPlanLoading(true);
    setPlanError(null);
    setSelectedPlanId("");
    try {
      const params = new URLSearchParams();
      params.append("limit", "100");
      params.append("offset", "0");
      if (planVendorFilter?.trim()) params.append("vendor", planVendorFilter.trim());
      if (planSearch?.trim()) params.append("search", planSearch.trim());
      const data = await api.get(`/admin/subscription-plans?${params.toString()}`);
      setPlanList(data.data || []);
    } catch (err) {
      setPlanError(err.message || "Failed to fetch subscription plans");
    } finally {
      setPlanLoading(false);
    }
  }, [api, planVendorFilter, planSearch]);

  React.useEffect(() => {
    if (orgModalOpen) fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fetch on modal open, not on orgSearch change
  }, [orgModalOpen]);

  React.useEffect(() => {
    if (planModalOpen) fetchSubscriptionPlans();
  }, [planModalOpen, planVendorFilter, fetchSubscriptionPlans]);

  const planSearchTimeoutRef = React.useRef(null);
  React.useEffect(() => {
    if (!planModalOpen) return;
    if (planSearchTimeoutRef.current) clearTimeout(planSearchTimeoutRef.current);
    planSearchTimeoutRef.current = setTimeout(() => fetchSubscriptionPlans(), 300);
    return () => { if (planSearchTimeoutRef.current) clearTimeout(planSearchTimeoutRef.current); };
  }, [planSearch, planModalOpen, fetchSubscriptionPlans]);

  const orgSearchTimeoutRef = React.useRef(null);
  React.useEffect(() => {
    if (!orgModalOpen) return;
    if (orgSearchTimeoutRef.current) clearTimeout(orgSearchTimeoutRef.current);
    orgSearchTimeoutRef.current = setTimeout(() => fetchOrganizations(), 300);
    return () => { if (orgSearchTimeoutRef.current) clearTimeout(orgSearchTimeoutRef.current); };
  }, [orgSearch, orgModalOpen, fetchOrganizations]);

  const handleApplyOrganization = () => {
    if (!selectedOrgId) return;
    mergeIntoPayload({ organization_id: selectedOrgId });
    setOrgModalOpen(false);
  };

  const handleApplySubscriptionPlan = () => {
    if (!selectedPlanId) return;
    mergeIntoPayload({ subscription_plan_id: selectedPlanId });
    setPlanModalOpen(false);
  };

  const handleLoadTemplate = () => {
    const templateJson = JSON.stringify(getDefaultTemplate(), null, 2);
    setOrderJson(templateJson);
    validateJsonFormat(templateJson);
    // Clear any existing messages and steps
    setValidation({
      errors: [],
      warnings: [],
      status: null,
    });
    clearStepsAndMessages();
  };

  const handleValidate = async () => {
    if (!orderJson.trim()) {
      setValidation({
        errors: ["Order JSON is required"],
        warnings: [],
        status: "failed",
      });
      return;
    }

    // JSON format should already be validated, but double-check
    if (!isValidJson) {
      setValidation({
        errors: ["Invalid JSON format. Please check your JSON syntax."],
        warnings: [],
        status: "failed",
      });
      return;
    }

    let parsedJson;
    try {
      parsedJson = JSON.parse(orderJson);
    } catch {
      setValidation({
        errors: ["Invalid JSON format. Please check your JSON syntax."],
        warnings: [],
        status: "failed",
      });
      return;
    }

    setValidating(true);
    setValidation({
      errors: [],
      warnings: [],
      status: null,
    });
    clearStepsAndMessages();

    try {
      const result = await api.post("/admin/squarespace/validate", parsedJson);
      
      // Update validation state with response
      setValidation({
        errors: result.errors || [],
        warnings: result.warnings || [],
        status: result.status || "validated",
      });
    } catch (err) {
      console.error("Error validating order:", err);
      const errData = err.data || {};
      let validationErrors = Array.isArray(errData.errors)
        ? errData.errors
        : errData.error
          ? [errData.error]
          : errData.message
            ? [errData.message]
            : [err.message || "Validation failed"];
      // Ensure each error is a string (API may return objects)
      validationErrors = validationErrors.map((e) =>
        typeof e === "string" ? e : e?.message || String(e)
      );
      setValidation({
        errors: validationErrors,
        warnings: errData.warnings || [],
        status: "failed",
      });
    } finally {
      setValidating(false);
    }
  };

  const handleProcessOrder = async () => {
    if (!orderJson.trim()) {
      return;
    }

    if (!orderToken) {
      addMessage("VITE_ORDER_TOKEN is not configured. Add it to admin .env and restart.", "error");
      return;
    }

    // Validate JSON format
    let parsedJson;
    try {
      parsedJson = JSON.parse(orderJson);
    } catch {
      return;
    }

    setProcessing(true);
    clearStepsAndMessages();
    addStep("Parse JSON", "completed");
    addStep("Store Payload", "in_progress");
    addMessage("Submitting order payload...", "info");

    try {
      const result = await api.post(
        `/admin/squarespace/orders/process?token=${encodeURIComponent(orderToken)}`,
        parsedJson
      );
      const successMsg = result.message || "Order submitted";
      updateStep("Store Payload", "completed");
      addMessage(successMsg, "success");

      if (result.data?.id) {
        addMessage("Payload ID: ", "info", {
          linkTo: `/dashboard/squarespace/payloads/${result.data.id}`,
          linkLabel: result.data.id,
        });
        addMessage(`Status: ${result.data.status || "New"}`, "info");
      }

      setOrderJson("");
    } catch (err) {
      console.error("Error submitting order:", err);
      addMessage(`Error: ${err.message}`, "error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Container maxWidth="lg" sx={{ mt: "48px", mb: 4, flexGrow: 1, pt: 0, px: 2 }}>
            <Box sx={{ mb: 2, mt: 0 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Submit Order
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Submit Order"
              description="Submit Squarespace orders to be stored for processing"
              icon={<PlayArrowIcon sx={{ color: "primary.main" }} />}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography
                    component="code"
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      wordBreak: "break-all",
                    }}
                  >
                    POST {processOrderUrlWithToken}
                  </Typography>
                  <Tooltip title={copiedUrl ? "Copied!" : "Copy full URL"}>
                    <IconButton size="small" onClick={handleCopyEndpoint} color="inherit">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    VITE_ORDER_TOKEN (sent with request):
                  </Typography>
                  <Typography
                    component="code"
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.7rem",
                      color: orderToken ? "text.primary" : "text.secondary",
                      opacity: orderToken ? 1 : 0.8,
                    }}
                  >
                    {orderToken || "(not set — add VITE_ORDER_TOKEN to .env and restart)"}
                  </Typography>
                  {orderToken && (
                    <Tooltip title={copiedToken ? "Copied!" : "Copy token"}>
                      <IconButton size="small" onClick={handleCopyToken} color="inherit" sx={{ p: 0.25 }}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </SquarespacePageHeader>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        Submit Order
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<BusinessIcon />}
                          onClick={() => setOrgModalOpen(true)}
                          disabled={processing || validating}
                        >
                          Find Org
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CategoryIcon />}
                          onClick={() => setPlanModalOpen(true)}
                          disabled={processing || validating}
                        >
                          Find Plan
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DescriptionIcon />}
                          onClick={handleLoadTemplate}
                          disabled={processing || validating}
                        >
                          Load Template
                        </Button>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Order JSON"
                        placeholder='{"email": "...", "firstName": "...", "lastName": "...", "country": "...", "ipAddress": "...", "title": "...", "organization_id": "...", "subscription_plan_id": "..."}'
                        value={orderJson}
                        onChange={handleJsonChange}
                        disabled={processing || validating}
                        variant="outlined"
                        multiline
                        rows={20}
                        error={!isValidJson && orderJson.trim() !== ""}
                        helperText={
                          !isValidJson && orderJson.trim() !== ""
                            ? "Invalid JSON format"
                            : ""
                        }
                        sx={{
                          "& .MuiInputBase-input": {
                            fontFamily: "monospace",
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setOrderJson("");
                          setValidation({
                            errors: [],
                            warnings: [],
                            status: null,
                          });
                          clearStepsAndMessages();
                        }}
                        disabled={processing || validating || !orderJson.trim()}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        startIcon={validating ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                        onClick={handleValidate}
                        disabled={processing || validating || !orderJson.trim() || !isValidJson}
                      >
                        {validating ? "Validating..." : "Validate"}
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={processing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                        onClick={handleProcessOrder}
                        disabled={processing || validating || !orderJson.trim() || !isValidJson || validation.status !== "validated" || !orderToken}
                      >
                        {processing ? "Submitting..." : "Submit Order"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {validation.status ? "Validation Results" : "Processing Status"}
                      </Typography>
                      {validation.status && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CodeIcon />}
                          onClick={() => setValidationJsonModalOpen(true)}
                        >
                          View JSON
                        </Button>
                      )}
                    </Box>
                    
                    {/* Validation Results */}
                    {validation.status && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={validation.status === "validated" ? "Validated" : "Failed"}
                            color={validation.status === "validated" ? "success" : "error"}
                            sx={{ fontWeight: "bold" }}
                          />
                        </Box>

                        {validation.errors.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="error" fontWeight="bold" gutterBottom>
                              Errors:
                            </Typography>
                            <List dense>
                              {validation.errors.map((err, index) => (
                                <ListItem key={index} sx={{ px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={err} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {validation.warnings.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="warning.main" fontWeight="bold" gutterBottom>
                              Warnings:
                            </Typography>
                            <List dense>
                              {validation.warnings.map((warn, index) => (
                                <ListItem key={index} sx={{ px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <InfoIcon color="warning" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={warn} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {validation.status === "validated" && validation.errors.length === 0 && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            Order is validated and ready to process
                          </Alert>
                        )}
                      </Box>
                    )}

                    {/* Processing Steps */}
                    {steps.length > 0 && (
                      <>
                        {validation.status && <Divider sx={{ my: 2 }} />}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Steps
                          </Typography>
                          <List dense>
                            {steps.map((step, index) => (
                              <ListItem key={index} sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  {step.status === "completed" ? (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                  ) : step.status === "error" ? (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  ) : step.status === "in_progress" ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={step.name}
                                  secondary={step.timestamp.toLocaleTimeString()}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </>
                    )}

                    {/* Processing Messages */}
                    {messages.length > 0 && (
                      <>
                        {(validation.status || steps.length > 0) && <Divider sx={{ my: 2 }} />}
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Messages
                          </Typography>
                          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                            {messages.map((msg, index) => (
                              <Box
                                key={index}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor:
                                    msg.type === "error"
                                      ? "error.light"
                                      : msg.type === "success"
                                      ? "success.light"
                                      : "info.light",
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                  {msg.type === "error" ? (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  ) : msg.type === "success" ? (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                  ) : (
                                    <InfoIcon color="info" fontSize="small" />
                                  )}
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" component="div">
                                      {msg.message}
                                      {msg.linkTo && msg.linkLabel && (
                                        <>
                                          {" "}
                                          <Box
                                            component={Link}
                                            to={msg.linkTo}
                                            sx={{
                                              color: "primary.main",
                                              fontWeight: 500,
                                              textDecoration: "none",
                                              "&:hover": { textDecoration: "underline" },
                                            }}
                                          >
                                            {msg.linkLabel}
                                          </Box>
                                        </>
                                      )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {msg.timestamp.toLocaleTimeString()}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </>
                    )}

                    {!validation.status && steps.length === 0 && messages.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        Validation results and processing status will appear here.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Validation JSON Modal */}
      <JsonDataModal
        open={validationJsonModalOpen}
        onClose={() => setValidationJsonModalOpen(false)}
        title="Validation Response"
        data={validation}
      />

      {/* Find Org Modal */}
      <Dialog
        open={orgModalOpen}
        onClose={() => { setOrgModalOpen(false); setOrgSearch(""); setSelectedOrgId(""); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: "85vh" } }}
      >
        <DialogTitle>Find and Set Organization</DialogTitle>
        <DialogContent dividers>
          {orgError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orgError}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by organization name..."
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ minHeight: 320, maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Organization Name</TableCell>
                  <TableCell>DBA</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`org-skeleton-${i}`}>
                      <TableCell><Skeleton height={20} width="80%" /></TableCell>
                      <TableCell><Skeleton height={20} width={60} /></TableCell>
                      <TableCell><Skeleton height={20} width={40} /></TableCell>
                      <TableCell><Skeleton height={20} width={120} /></TableCell>
                    </TableRow>
                  ))
                ) : orgList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No organizations found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orgList.map((org) => (
                    <TableRow
                      key={org.id}
                      hover
                      onClick={() => setSelectedOrgId(selectedOrgId === org.id ? "" : org.id)}
                      selected={selectedOrgId === org.id}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{org.organization_name || org.name || org.legal_name || "N/A"}</TableCell>
                      <TableCell>{org.dba_name || "—"}</TableCell>
                      <TableCell>{org.formation_state || org.physical_state || org.state || "—"}</TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {org.id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApplyOrganization}
            disabled={!selectedOrgId || orgLoading}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Find Plan Modal */}
      <Dialog
        open={planModalOpen}
        onClose={() => { setPlanModalOpen(false); setSelectedPlanId(""); setPlanVendorFilter(""); setPlanSearch(""); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: "85vh" } }}
      >
        <DialogTitle>Find and Set Subscription Plan</DialogTitle>
        <DialogContent dividers>
          {planError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {planError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by plan name..."
              value={planSearch}
              onChange={(e) => setPlanSearch(e.target.value)}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={planVendorFilter}
                onChange={(e) => setPlanVendorFilter(e.target.value)}
                label="Vendor"
              >
                <MenuItem value="">All</MenuItem>
                {SUBSCRIPTION_PLANS_VENDORS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ minHeight: 320, maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Plan Name</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Term</TableCell>
                  <TableCell>Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {planLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`plan-skeleton-${i}`}>
                      <TableCell><Skeleton height={20} width="70%" /></TableCell>
                      <TableCell><Skeleton height={20} width={100} /></TableCell>
                      <TableCell><Skeleton height={20} width={60} /></TableCell>
                      <TableCell><Skeleton height={20} width={50} /></TableCell>
                    </TableRow>
                  ))
                ) : planList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No subscription plans found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  planList.map((plan) => (
                    <TableRow
                      key={plan.id}
                      hover
                      onClick={() => setSelectedPlanId(selectedPlanId === plan.id ? "" : plan.id)}
                      selected={selectedPlanId === plan.id}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{plan.name || "N/A"}</TableCell>
                      <TableCell>{plan.vendor || "N/A"}</TableCell>
                      <TableCell>{plan.term || "N/A"}</TableCell>
                      <TableCell>
                        {plan.price != null ? `$${parseFloat(plan.price).toFixed(2)}` : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApplySubscriptionPlan}
            disabled={!selectedPlanId || planLoading}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
